import { CropperDialogProps } from '.';
import { Cropper } from './Cropper';
import Cropperjs from 'cropperjs';
import { Dialog } from '../../Dialog';
import { createElement, useRef, useState } from 'react';
import { InnerToolbar } from '../../Toolbar/__internal__/InnerToolbar/InnerToolbar';
import { ToolbarSpacer } from '../../Toolbar/__internal__';
import { Button, ButtonVariant } from '../../Button';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { postfixTestId } from '../../../util/testId';
import {
  ArrowSmallDown,
  ArrowSmallLeft,
  ArrowSmallRight,
  ArrowSmallUp,
  Reset,
  ZoomIn,
  ZoomOut,
} from '@sas/react-svg';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import cx from 'classnames';
import cn from './CropperDialog.css.json';
import './CropperDialog.css';

const MOVE_OFFEST = 5;
const ZOOM_OFFEST = 0.1;

const useCropDialogId = createUseHtmlId('CropDialog');

/**
 * @internal
 */
export const CropperDialog = (props: CropperDialogProps) => {
  const isRTL = useIsRTL();
  const localize = useNCTextLocalizer('ImageSelector');
  const descriptionId = useCropDialogId();

  const cropperRef = useRef<Cropperjs | null>(null);
  const isCropperReadyRef = useRef(false);
  const [cropperKey, setCropperKey] = useState(Date.now());

  const onMoveCropBox = (type: 'up' | 'down' | 'left' | 'right') => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    const { top, left, height, width } = cropper.getCropBoxData();
    const moveCanvas = (xOffset: number, yOffset: number) => {
      cropper.move(xOffset, yOffset);
    };
    const moveCropBox = (xOffset: number, yOffset: number) => {
      const cropBoxData = cropper.getCropBoxData();
      cropBoxData.top += yOffset;
      cropBoxData.left += xOffset;
      cropper.setCropBoxData(cropBoxData);
    };

    switch (type) {
      case 'up':
        if (top <= 0) {
          moveCanvas(0, MOVE_OFFEST);
        } else {
          moveCropBox(0, -MOVE_OFFEST);
        }
        break;
      case 'down':
        if (top + height >= cropper.getContainerData().height) {
          moveCanvas(0, -MOVE_OFFEST);
        } else {
          moveCropBox(0, MOVE_OFFEST);
        }
        break;
      case isRTL ? 'right' : 'left':
        if (left <= 0) {
          moveCanvas(MOVE_OFFEST, 0);
        } else {
          moveCropBox(-MOVE_OFFEST, 0);
        }
        break;
      case isRTL ? 'left' : 'right':
        if (left + width >= cropper.getContainerData().width) {
          moveCanvas(-MOVE_OFFEST, 0);
        } else {
          moveCropBox(MOVE_OFFEST, 0);
        }
        break;
    }
  };

  const onReset = () => {
    const cropper = cropperRef.current;
    if (!cropper || !isCropperReadyRef.current) return;

    cropper.reset();

    // Fill the crop box to the maximum size of the crop area and
    // position in the center both vertically and horizontally.
    const imageData = cropper.getImageData();
    const containerData = cropper.getContainerData();

    cropper.setCropBoxData({ width: imageData.width, height: imageData.height });

    const cropBoxData = cropper.getCropBoxData();
    cropper.setCropBoxData({
      ...cropBoxData,
      left: (containerData.width - cropBoxData.width) / 2,
      top: (containerData.height - cropBoxData.height) / 2,
    });
  };
  const [setDialogContent] = useResizeObserverRef<HTMLDivElement>(() => {
    // Force re-render the Cropper and the preview image
    setCropperKey(Date.now());
  });

  const dt = useDesignTokens();
  const style = renderInlineCssVars(
    {
      'row-gap': dt.spacing.core.s,
      'column-gap': dt.spacing.core.m,
    },
    'cropper-dialog'
  );

  return (
    <Dialog
      testId={props.testId}
      isOpen={props.isOpen}
      title={localize('cropDialog.title')}
      size={{
        maxWidth: 'calc(100% - 4rem)',
        maxHeight: 'calc(100% - 4rem)',
        width: 'calc(100% - 30rem)',
        height: 'calc(100% - 10rem)',
        minWidth: '45rem',
        minHeight: '30rem',
      }}
      firstAction={{
        text: localize('cropDialog.confirmButton.label'),
        onPress: () => {
          if (!cropperRef.current) return;
          props.onCrop?.(cropperRef.current.getCroppedCanvas());
        },
        initialFocus: true,
      }}
      dismissAction={{
        text: localize('cropDialog.dismissButton.label'),
        onPress: props.onDismiss,
      }}
      style={style}
      ariaDescribedBy={descriptionId}
      busyStateIndicatorProps={{ active: props.isBusy, delay: 0 }}
    >
      <div className={cn.dialogContent} ref={setDialogContent}>
        <Cropper
          cropperKey={cropperKey}
          src={props.src}
          cropperRef={cropperRef}
          ellipse={props.ellipse}
          aspectRatio={props.aspectRatio}
          previewClassName={cn.preview}
          onReady={() => {
            isCropperReadyRef.current = true;
            onReset();
          }}
          onError={props.onError}
          className={cn.cropper}
        />
        <div className={cn.bar}>
          <Button
            testId={postfixTestId(props.testId, 'zoomInButton')}
            variant={ButtonVariant.Icon}
            icon={{
              icon: ZoomIn,
              tooltip: localize('cropDialog.zoomInButton.tooltip.label'),
              ariaLabel: localize('cropDialog.zoomInButton.aria'),
            }}
            onPress={() => cropperRef.current?.zoom(ZOOM_OFFEST)}
          />
          <Button
            testId={postfixTestId(props.testId, 'zoomOutButton')}
            variant={ButtonVariant.Icon}
            icon={{
              icon: ZoomOut,
              tooltip: localize('cropDialog.zoomOutButton.tooltip.label'),
              ariaLabel: localize('cropDialog.zoomOutButton.aria'),
            }}
            onPress={() => cropperRef.current?.zoom(-ZOOM_OFFEST)}
          />
          <ToolbarSpacer />
          <InnerToolbar
            items={[
              {
                type: 'Button',
                testId: postfixTestId(props.testId, 'moveLeftButton'),
                variant: ButtonVariant.Icon,
                icon: {
                  icon: ArrowSmallLeft,
                  tooltip: localize('cropDialog.moveLeftButton.tooltip.label'),
                  ariaLabel: localize('cropDialog.moveLeftButton.aria'),
                },
                onPress: () => onMoveCropBox('left'),
                enableContinuousPress: true,
              },
              {
                type: 'Button',
                testId: postfixTestId(props.testId, 'moveRightButton'),
                variant: ButtonVariant.Icon,
                icon: {
                  icon: ArrowSmallRight,
                  tooltip: localize('cropDialog.moveRightButton.tooltip.label'),
                  ariaLabel: localize('cropDialog.moveRightButton.aria'),
                },
                onPress: () => onMoveCropBox('right'),
                enableContinuousPress: true,
              },
              {
                type: 'Button',
                testId: postfixTestId(props.testId, 'moveUpButton'),
                variant: ButtonVariant.Icon,
                icon: {
                  icon: ArrowSmallUp,
                  tooltip: localize('cropDialog.moveUpButton.tooltip.label'),
                  ariaLabel: localize('cropDialog.moveUpButton.aria'),
                },
                onPress: () => onMoveCropBox('up'),
                enableContinuousPress: true,
              },
              {
                type: 'Button',
                testId: postfixTestId(props.testId, 'moveDownButton'),
                variant: ButtonVariant.Icon,
                icon: {
                  icon: ArrowSmallDown,
                  tooltip: localize('cropDialog.moveDownButton.tooltip.label'),
                  ariaLabel: localize('cropDialog.moveDownButton.aria'),
                },
                onPress: () => onMoveCropBox('down'),
                enableContinuousPress: true,
              },
            ]}
          />
          <ToolbarSpacer />
          <Button
            testId={postfixTestId(props.testId, 'resetButton')}
            icon={{
              icon: Reset,
              tooltip: localize('cropDialog.resetButton.tooltip.label'),
              ariaLabel: localize('cropDialog.resetButton.aria'),
            }}
            onPress={onReset}
            variant={ButtonVariant.Icon}
          />
        </div>
        <div className={cn.description} id={descriptionId}>
          {localize('cropDialog.desc')}
        </div>
        <div className={cx(cn.preview, props.ellipse && cn.ellipse)} key={cropperKey} />
      </div>
    </Dialog>
  );
};
